import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from "react";

interface BarcodeScannerContextValue {
  isOpen: boolean;
  openScanner: () => Promise<string | null>;
  closeScanner: () => void;
  resolve: (barcode: string | null) => void;
}

const BarcodeScannerContext = createContext<BarcodeScannerContextValue | null>(null);

export function BarcodeScannerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const resolveRef = useRef<((barcode: string | null) => void) | null>(null);

  const openScanner = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(null);
    }
    return new Promise<string | null>((res) => {
      resolveRef.current = res;
      setIsOpen(true);
    });
  }, []);

  const closeScanner = useCallback(() => {
    setIsOpen(false);
    if (resolveRef.current) {
      resolveRef.current(null);
      resolveRef.current = null;
    }
  }, []);

  const resolve = useCallback((barcode: string | null) => {
    setIsOpen(false);
    if (resolveRef.current) {
      resolveRef.current(barcode);
      resolveRef.current = null;
    }
  }, []);

  return (
    <BarcodeScannerContext.Provider value={{ isOpen, openScanner, closeScanner, resolve }}>
      {children}
    </BarcodeScannerContext.Provider>
  );
}

export function useBarcodeScanner() {
  const ctx = useContext(BarcodeScannerContext);
  if (!ctx) throw new Error("useBarcodeScanner must be used within BarcodeScannerProvider");
  return ctx;
}
